import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAppData } from '../context/AppDataContext';
import NumberInput from './NumberInput';
import { PAGE_MODE, getPageScale } from '../utils/pageScale';

const segmentClass = (active) => [
  'px-2.5 py-1 transition-colors disabled:cursor-wait',
  active
    ? 'bg-ink-900 text-white dark:bg-ink-50 dark:text-ink-950'
    : 'text-ink-500 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100'
].join(' ');

/**
 * 종이책 쪽수 ↔ 전자책 쪽수 전환. 책 상세의 정보 탭까지 가지 않고 목록에서 바로 바꾼다.
 *
 * 저장된 진도와 인용은 늘 기준 쪽수라서, 바꿔도 화면에 보이는 숫자와 입력 단위만 달라진다.
 * 전자책 총 쪽수를 아직 모르면 그 자리에서 물어보고, 넣는 즉시 전자책으로 바꾼다.
 */
const PageModeSwitch = ({ book }) => {
  const { updateBook } = useAppData();
  const [asking, setAsking] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  // 기준 쪽수가 없으면 환산할 수 없다
  if (!(book.totalPages > 0)) return null;

  const { useEbook } = getPageScale(book);
  const ebookTotal = parseInt(value, 10);
  const validTotal = Number.isFinite(ebookTotal) && ebookTotal >= 1;

  const save = async (changes, message) => {
    setSaving(true);
    try {
      await updateBook(book.id, changes);
      toast.success(message, { id: `page-mode-${book.id}` });
      return true;
    } catch (error) {
      toast.error(error.message || '바꾸지 못했어요');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toPrint = () => {
    if (!useEbook || saving) return;
    setAsking(false);
    save({ pageMode: PAGE_MODE.PRINT }, `종이책 ${book.totalPages}쪽 기준으로 봐요`);
  };

  const toEbook = () => {
    if (useEbook || saving) return;
    if (book.ebookTotalPages > 0) {
      save({ pageMode: PAGE_MODE.EBOOK }, `전자책 ${book.ebookTotalPages}쪽 기준으로 봐요`);
    } else {
      setAsking(current => !current);
    }
  };

  const saveEbookTotal = async () => {
    if (!validTotal || saving) return;
    const ok = await save(
      { ebookTotalPages: ebookTotal, pageMode: PAGE_MODE.EBOOK },
      `전자책 ${ebookTotal}쪽 기준으로 봐요`
    );
    if (ok) {
      setAsking(false);
      setValue('');
    }
  };

  return (
    <div className="relative shrink-0">
      <div
        role="group"
        aria-label={`${book.title} 쪽수 기준`}
        className="inline-flex overflow-hidden rounded border border-ink-200 dark:border-ink-800 text-[11px] font-sans"
      >
        <button
          type="button"
          onClick={toPrint}
          disabled={saving}
          aria-pressed={!useEbook}
          title={`종이책 쪽수 (${book.totalPages}쪽)로 보고 입력`}
          className={segmentClass(!useEbook)}
        >
          종이책
        </button>
        <button
          type="button"
          onClick={toEbook}
          disabled={saving}
          aria-pressed={useEbook}
          aria-expanded={book.ebookTotalPages > 0 ? undefined : asking}
          title={book.ebookTotalPages > 0
            ? `전자책 쪽수 (${book.ebookTotalPages}쪽)로 보고 입력`
            : '전자책 전체 쪽수를 넣고 전자책 쪽수로 보기'}
          className={`border-l border-ink-200 dark:border-ink-800 ${segmentClass(useEbook)}`}
        >
          전자책
        </button>
      </div>

      {/* 전자책 전체 쪽수를 처음 넣는 곳. 줄 배치를 흔들지 않게 아래로 띄운다 */}
      {asking && (
        <div className="absolute right-0 top-full z-20 mt-2 w-60 rounded-md border border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-950 p-3 shadow-lg">
          <p className="text-xs text-ink-700 dark:text-ink-200 font-sans">
            내 기기에서 보이는 전자책 전체 쪽수
          </p>
          <div className="mt-2 flex items-center gap-2">
            <NumberInput
              min="1"
              value={value}
              autoFocus
              onChange={e => setValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); saveEbookTotal(); }
                if (e.key === 'Escape') setAsking(false);
              }}
              placeholder="예: 452"
              aria-label="전자책 전체 쪽수"
              className="!w-24 !px-2"
            />
            <span className="text-xs text-ink-600 dark:text-ink-300 font-sans">쪽</span>
            <button
              type="button"
              onClick={saveEbookTotal}
              disabled={!validTotal || saving}
              className="btn-primary ml-auto !px-3 !py-1.5 text-xs disabled:opacity-40"
            >
              저장
            </button>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-ink-500 dark:text-ink-400 font-sans">
            기록은 종이책 {book.totalPages}쪽 기준으로 저장되어, 나중에 바꿔도 그대로 남아요.
          </p>
        </div>
      )}
    </div>
  );
};

export default PageModeSwitch;
